<template>
    <section v-if="track && canExport" class="export_panel">
        <ToggleButtonBigger
            :text="$t('exportPanel.applyEffects')"
            :subtitle="$t('exportPanel.applyEffectsSubtitle')"
            v-model="exportWithEffects"
        />

        <div class="export_format mt-3">
            <ToggleButtonBigger
                :text="$t('exportPanel.exportWav')"
                :subtitle="'PCM 16-bit'"
                v-model="exportFormatWav"
            />
            <ToggleButtonBigger
                :text="$t('exportPanel.exportMp3')"
                :subtitle="'MP3 320 kbps'"
                v-model="exportFormatMp3"
            />
        </div>

        <div class="export_actions mt-4">
            <button class="btn-new" type="button" :disabled="isExporting" @click="exportTrack">
                <span v-if="isExporting" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <i v-else class="bi bi-file-earmark-music-fill"></i>
                {{ isExporting ? $t('exportPanel.exporting') : $t('common.export') }}
            </button>
            <button class="btn-new" type="button" :disabled="isExporting || isCreatingExportTrack" @click="exportAsNewTrack">
                <span v-if="isCreatingExportTrack" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <i v-else class="bi bi-plus-circle"></i>
                {{ isCreatingExportTrack ? $t('exportPanel.creatingTrack') : $t('exportPanel.exportAsNewTrack') }}
            </button>
            <button class="btn-new" type="button" @click="downloadOriginal">
                <i class="bi bi-cloud-arrow-down-fill"></i> {{ $t('exportPanel.downloadOriginal') }}
            </button>
        </div>
    </section>
</template>

<script>
import ToggleButtonBigger from '@/components/UI/ToggleButtonBigger.vue';
import storageService from '@/scripts/services/StorageService.js';
import { playerActions, playerStore } from '@/scripts/services/PlayerService.js';
import { uuidv4 } from '@/scripts/uuid.js';
import {
    ANALYSIS_TARGET_SAMPLE_RATE,
    analyzeAudio,
    dataUrlToBlob,
    generateWaveformDataUrlFromChannels,
    prepareAudioForAnalysis
} from '@/scripts/utils/trackAudioAnalysis.js';

export default {
    name: 'TrackExportPanel',
    components: { ToggleButtonBigger },
    props: {
        track: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            exportWithEffects: true,
            exportFormat: 'wav',
            isCreatingExportTrack: false
        };
    },
    computed: {
        isExporting() {
            return playerStore.isExporting || playerStore.isExportingMp3;
        },
        canExport() {
            return storageService.state.storageType !== 'shared';
        },
        exportFormatWav: {
            get() {
                return this.exportFormat === 'wav';
            },
            set(value) {
                if (value) this.exportFormat = 'wav';
            }
        },
        exportFormatMp3: {
            get() {
                return this.exportFormat === 'mp3';
            },
            set(value) {
                if (value) this.exportFormat = 'mp3';
            }
        }
    },
    methods: {
        exportTrack() {
            playerActions.exportTrackWithEffects(this.track, this.exportFormat, this.exportWithEffects);
        },
        async exportAsNewTrack() {
            if (!this.track || this.isCreatingExportTrack || this.isExporting) {
                return;
            }

            this.isCreatingExportTrack = true;
            try {
                const exported = await playerActions.exportTrackWithEffects(
                    this.track,
                    this.exportFormat,
                    this.exportWithEffects,
                    false
                );
                if (!exported) {
                    throw new Error(this.$t('exportPanel.exportAudioFailed'));
                }

                const audioData = await exported.blob.arrayBuffer();
                const AudioContext = window.AudioContext || window.webkitAudioContext;
                const audioContext = new AudioContext();
                let audioBuffer;
                try {
                    audioBuffer = await audioContext.decodeAudioData(audioData.slice(0));
                } finally {
                    await audioContext.close();
                }

                const preparedAudio = prepareAudioForAnalysis(audioBuffer, ANALYSIS_TARGET_SAMPLE_RATE);
                const [analysisResults, waveformDataUrl] = await Promise.all([
                    analyzeAudio(preparedAudio),
                    Promise.resolve(generateWaveformDataUrlFromChannels(preparedAudio.left, preparedAudio.right))
                ]);
                if (!waveformDataUrl) {
                    throw new Error(this.$t('exportPanel.waveformFailed'));
                }

                const exportedName = exported.filename;
                const file = await storageService.uploadFile(audioData, exportedName);
                const waveformPayload = storageService.state.storageType === 'remote'
                    ? dataUrlToBlob(waveformDataUrl)
                    : waveformDataUrl;
                const waveform = await storageService.uploadFile(waveformPayload, `waveform_${exportedName}.png`);
                const id = uuidv4();
                const created = await storageService.createTrack({
                    id,
                    file,
                    size: exported.blob.size,
                    filename: exportedName,
                    upload_date: new Date().toISOString(),
                    bpm: analysisResults.bpm,
                    key: analysisResults.scale.key,
                    mode: analysisResults.scale.mode,
                    duration: Math.round(audioBuffer.duration * 1000),
                    lufs: analysisResults.lufs == null ? null : Math.round(analysisResults.lufs * 100),
                    waveform,
                    effects: '',
                    project: null,
                    num: null,
                    artist: this.track.artist,
                    name: `${this.track.name || this.$t('trackView.untitled')}${this.exportWithEffects ? this.$t('exportPanel.exportedSuffix') : this.$t('exportPanel.copySuffix')}`,
                    notes: ''
                });
                if (!created) {
                    throw new Error(this.$t('exportPanel.createTrackFailed'));
                }

                await this.$router.push(`/track/${id}`);
            } catch (error) {
                console.error('Error creating exported track:', error);
                alert(this.$t('exportPanel.createTrackAlert', { message: error.message }));
            } finally {
                this.isCreatingExportTrack = false;
            }
        },
        async downloadOriginal() {
            if (!this.track?.file) return;

            const filename = this.track.filename || this.track.name || 'track';
            try {
                const fileId = typeof this.track.file === 'object' ? this.track.file.id : this.track.file;
                const url = await storageService.getFileLink(fileId);
                if (!url) {
                    alert(this.$t('exportPanel.originalUnavailable'));
                    return;
                }
                this.triggerDownload(url, filename);
                if (url.startsWith('blob:')) {
                    setTimeout(() => storageService.revokeBlobUrl(url), 1000);
                }
            } catch (error) {
                console.error('Error downloading original file:', error);
                alert(this.$t('exportPanel.downloadOriginalFailed'));
            }
        },
        triggerDownload(url, filename) {
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            link.remove();
        }
    }
};
</script>

<style scoped>
.export_panel {
    min-height: 100%;
}

.export_actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
}

.export_actions .btn-new {
    margin: 0;
}
</style>
